import { useState, createContext, useContext } from 'react'
import { motion } from 'framer-motion'
import { Brain, Trophy, MessageCircle, Info } from 'lucide-react'

// Audience context for app-wide mode switching
const AudienceContext = createContext({
  audience: 'casual',
  setAudience: () => {},
})

export const useAudience = () => useContext(AudienceContext)

export function AudienceProvider({ children }) {
  const [audience, setAudience] = useState('casual')

  return (
    <AudienceContext.Provider value={{ audience, setAudience }}>
      {children}
    </AudienceContext.Provider>
  )
}

const audiences = [
  {
    id: 'data_scientist',
    label: 'Data Science',
    shortLabel: 'Stats',
    icon: Brain,
    description: 'Statistical methodology, model details, confidence intervals',
    color: 'accent-purple',
  },
  {
    id: 'sports_fanatic',
    label: 'Sports Fan',
    shortLabel: 'Fan',
    icon: Trophy,
    description: 'Betting insights, trends, matchup analysis',
    color: 'accent-cyan',
  },
  {
    id: 'casual',
    label: 'Simple',
    shortLabel: 'Easy',
    icon: MessageCircle,
    description: 'Plain English, talking points, easy to understand',
    color: 'success',
  },
]

export function AudienceToggle({ compact = false }) {
  const { audience, setAudience } = useAudience()

  if (compact) {
    return (
      <div className="flex items-center gap-1 bg-dark-800/50 rounded-lg p-1">
        {audiences.map((a) => (
          <button
            key={a.id}
            onClick={() => setAudience(a.id)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              audience === a.id
                ? `bg-${a.color}/20 text-${a.color} border border-${a.color}/30`
                : 'text-dark-400 hover:text-dark-200'
            }`}
            title={a.description}
          >
            {a.shortLabel}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-dark-400">
        <Info className="w-4 h-4" />
        <span className="text-sm">Choose your viewing mode:</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {audiences.map((a) => {
          const Icon = a.icon
          const isActive = audience === a.id

          return (
            <motion.button
              key={a.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setAudience(a.id)}
              className={`relative p-4 rounded-xl border transition-all ${
                isActive
                  ? `bg-${a.color}/10 border-${a.color}/50 text-${a.color}`
                  : 'bg-dark-800/30 border-dark-700/50 text-dark-300 hover:bg-dark-800/50'
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                <Icon className="w-6 h-6" />
                <span className="font-medium">{a.label}</span>
                <span className="text-xs text-dark-500 text-center">{a.description}</span>
              </div>
              {isActive && (
                <motion.div
                  layoutId="audience-indicator"
                  className={`absolute inset-0 rounded-xl border-2 border-${a.color}`}
                  initial={false}
                  transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }}
                />
              )}
            </motion.button>
          )
        })}
      </div>
    </div>
  )
}

// Content component that shows different text based on audience
export function AudienceContent({ content }) {
  const { audience } = useAudience()

  if (!content) return null

  // content should be { data_scientist: '...', sports_fanatic: '...', casual: '...' }
  const text = content[audience] || content.casual || content

  return <>{text}</>
}

// Tooltip with term explanation
export function TermTooltip({ term, children }) {
  const { audience } = useAudience()
  const [showTooltip, setShowTooltip] = useState(false)

  const explanations = {
    elo: {
      data_scientist: 'Elo rating - zero-sum rating system where rating change = K × (actual - expected)',
      sports_fanatic: 'Power rating based on wins/losses - each win/loss adjusts the number',
      casual: 'A number showing how good a team is - higher means better',
    },
    win_probability: {
      data_scientist: 'P(win) derived from logistic function of Elo differential',
      sports_fanatic: 'Percentage chance of winning based on our model',
      casual: 'How likely we think they are to win',
    },
    spread: {
      data_scientist: 'Point spread - expected margin, set by market makers',
      sports_fanatic: 'How many points the favorite is expected to win by',
      casual: 'How much the favorite should win by',
    },
    sharp_money: {
      data_scientist: 'Professional bettor action causing line movement',
      sports_fanatic: 'When professional gamblers bet big on one side',
      casual: 'Smart money - when experts bet heavily',
    },
    ats: {
      data_scientist: 'Against the spread - binary outcome vs point spread',
      sports_fanatic: 'Whether a team covered the spread (beat expectations)',
      casual: 'Did they do better than expected?',
    },
  }

  const explanation = explanations[term]?.[audience] || explanations[term]?.casual || term

  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span className="border-b border-dashed border-dark-500 cursor-help">
        {children}
      </span>
      {showTooltip && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-sm text-dark-200 whitespace-nowrap z-50 shadow-lg"
        >
          {explanation}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-dark-800" />
        </motion.div>
      )}
    </span>
  )
}

export default AudienceToggle
